
/**
 *      Data-driven UI logic for JSON models and Backbone models
 *
 *      @author Irina Patrikeeva <irina.i.ipatrikeeva@nasa.gov>
 *
 *      Created on 06/02/2017
 *      Modified on 04/09/2020
 */

/**
 * Returns a JSON model for one of the page aspects needed to construct a page
 * @param modelType {string} - JSON model type
 * @param dataType {string} - data record type
 * @param section {string|null} - section name to return layout for that section; null to return the whole page layout
 * @returns {*}
 */
function getJSONModel( modelType, dataType, section ) {
    var model;
    if ( modelType === "record" ) {
        model = getDataModel( dataType, true );
    } else if ( modelType === "api" ) {
        model = getJSONAPI( dataType );
    } else if ( modelType === "layout" ) {
        model = getJSONLayout( dataType, section );
    } else if ( modelType === "view" ) {
        model = getJSONView( dataType );
    } else if ( modelType === "page" ) {
        model = getJSONPageState(dataType);
    }

    return model;
}

/**
 * Returns a JSON model for data records from the Data Model Registry
 * @param type {string} - data type
 * @param successCallback {Function} - function that gets called on success
 * @param failureCallback {Function} - function that gets called on failure
 */

function getJSONUIModel( type, successCallback, failureCallback ) {
    var url;
    if ( type ) {
        url = "/dmr/api/v1/" + type + "/ui";
        makeAjaxRequestWithRetry( url, successCallback, failureCallback, 1, 5 );
    }
}

/**
 * Returns a JSON API model
 * @param dataType {string} - data record type
 * @returns {*}
 */
function getJSONAPI( dataType ) {
    var model;
    if ( dataType === "part" ) {
        model = PartAPIModel;
    }
    else if ( dataType === "emupart" ) {
        model = EMUPartAPIModel;
    }
    else if ( dataType === "component" ) {
        model = ComponentAPIModel;
    }
    else if ( dataType === "emucomponent" ) {
        model = EMUComponentAPIModel;
    }
    else if ( GlobalPageState && GlobalPageState.currentPage === "record") {
        model = RecordAPIModel;
    }

    // Get models needed for the display of the page
    getAPIModels( model );

    return model;
}

/**
 * Get and store models from the API "model" key for the current page
 * @param apiModel {object} - API model for a data type
 */
function getAPIModels( apiModel ) {

    var properties,
        pageProperties,
        propertiesArray,
        sectionModel,
        model,
        i,
        j,
        k;

    if ( apiModel ) {

        properties = apiModel.properties;

        if ( properties && GlobalPageState ) {
            for ( i = 0; i < properties.length; i++ ) {
                pageProperties = properties[ i ];

                // Get the API model for the current page
                if ( pageProperties && pageProperties.page === GlobalPageState.currentPage ) {

                    propertiesArray = pageProperties.properties;

                    if ( propertiesArray ) {

                        // Look at each item in the properties array
                        // If an item has "model" key, fetch that model from DMR
                        for ( j = 0; j < propertiesArray.length; j++ ) {
                            sectionModel = propertiesArray[ j ];
                            if ( sectionModel ) {
                                model = sectionModel.model;
                                // If model is not specified, use current page type
                                if ( ! model ) {
                                    model = GlobalPageState.type;
                                }
                                if ( model ) {
                                    if ( sectionModel.multiple ) {
                                        for ( k = 0; k < model.length; k++ ) {
                                            getDataModel( model[ k ], true );
                                        }
                                    }  else {
                                        // Fetch data model and save it into global store
                                        getDataModel( model, true );
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Returns a JSON layout model
 * @param dataType {string} - data record type
 * @param section {string|null} - section name to return layout for; null to return the whole page layout
 * @returns {*}
 */
function getJSONLayout( dataType, section ) {
    var model;

    if ( section ) {
        // fetch only a portion of the layout
        if ( dataType === "part" || dataType === "emupart") {
            // fetch section of the part layout
            model = getSectionLayout( PartLayoutModel, section );
        } else if ( dataType === "component" || dataType === "emucomponent") {
            // fetch section of the component layout
            model = getSectionLayout( ComponentLayoutModel, section );
        } else {
            // fetch section of the default record layout
            model = getSectionLayout( RecordLayoutModel, section );
        }
    } else {
        // fetch the whole layout
        if (dataType === "part" || dataType === "emupart") {
            model = PartLayoutModel; // in the future, this will fetch the model from the server
        } else if (dataType === "component" || dataType === "emucomponent") {
            model = ComponentLayoutModel;
        } else {
            model = RecordLayoutModel;
        }
    }

    return model;
}

/**
 * Returns a JSON view model
 * @param dataType {string} - data record type
 * @returns {*}
 */
function getJSONView( dataType ) {
    var model;

    if ( GlobalPageState && GlobalPageState.currentPage === "record") {
        model = RecordViewModel; // Default record viewer model
    }

    if ( dataType === "part" || dataType === "emupart" ) {
        model = PartViewModel; // in the future, this will fetch the model from the server
    } else if ( dataType === "component" || dataType === "emucomponent" ) {
        model = ComponentViewModel;
    }
    return model;
}

/**
 * Returns a JSON page state model (only needed to part and component records)
 * @param dataType {string} - data record type
 * @returns {*}
 */
function getJSONPageState( dataType ) {
    var model;
    if ( dataType === "part" || dataType === "emupart" ) {
        model = PartPageStateModel; // in the future, this will fetch the model from the server
    } else if ( dataType === "component" || dataType === "emucomponent" ) {
        model = ComponentPageStateModel;
    }
    return model;
}

/**
 * Get a JSON record model based on the data object from the server
 * @param data {object} - ES response
 * @param pageName {string} - page name
 * @returns {*}
 */
function getJSONRecordModel( data, pageName ) {
    var model,
        key,
        result,
        type;
    if ( pageName && data ) {
        for ( key in data ) {
            if ( data.hasOwnProperty( key ) ) {
                if ( data[ key ] && data[ key ].hits && data[ key ].hits.hits ) {
                    if ( data[ key ].hits.hits.length > 0 ) {
                        // Take the first result to get the type ( does not work for search )
                        result = data[key].hits.hits[0];
                        type = result._type;
                        if ( type ) {
                            model = getJSONModel( "record", type, null );
                        }
                    }
                }
            }
        }
    }
    return model;
}

/**
 * Return a JSON record model based on the data name from the global page state
 * @param dataName {string} - data name
 * @returns {{}}
 * Examples:
 * "part" -> JSON Part Model
 * "component" -> JSON Component Model
 */
function getJSONDataModel(dataName) {
    var result = {},
        data = GlobalPageState.data,  // Check the global page state data attribute
        records = {};

    if ( data && dataName ) {

        records[ dataName ] = data[ dataName ];
        if ( hasKeys( records ) ) {
            result = getJSONRecordModel( records, GlobalPageState.currentPage );
        }
    }
    return result;
}

/**
 * Search for and return a model from a collection by model's name
 * @param collection {BackboneCollection} - collection of models
 * @param name {string} - name of the model
 * @returns {null|BackboneModel}
 */
function lookupFieldModel( collection, name ) {

    if ( ! collection ) {
        return null;
    }
    return _.find( collection.models, function ( model ) {
        if ( model ) {
            return model.get( "name" ) === name;
        } else {
            return null;
        }
    });
}

/**
 * Recursively lookup a field model from a collection of record field models
 * @param collection {BackboneCollection} - collection of models
 * @param name {string} - the name attribute of the model
 * @returns {null|BackboneModel}
 */
function lookupNestedFieldModel( collection, name ) {

    var nestedModel,
        valueModels,
        valueCollection;
    if ( ! collection ) {
        return null;
    }
    return _.find( collection.models, function( model ) {
        if ( model ) {
            if ( model.get("name") === name ) {
                return model;
            } else {
                // search nested models
                nestedModel = model.get("value");
                if ( nestedModel && typeof nestedModel ===  "object" ) {
                    valueModels = getBackboneRecordModels( nestedModel );
                    valueCollection = new BackboneCollection();
                    valueCollection.reset( valueModels );
                    return lookupNestedFieldModel( valueCollection, name );
                }
                else return null;
            }
        } else {
            return null;
        }
    });
}

/**
 * Display Search API document response using data model registry data model
 * @param response {object} - ES response object
 * @param viewModel {Backbone.Model} - page view model
 * @param names {[string]} - data section names from API model
 * @param type {string} - data type
 * @param urls {[string]} - array of data URLs
 * @returns {Function}
 */
function displayServerJSONModel( response, viewModel, names, type, urls ) {
    /**
     * Callback that is called when the call to fetch the model from Data Model Registry succeeds
     * @param data {object} - data model JSON object
     * @param textStatus {string} - status of the request
     * @param jqXHR {object} - jQuery XMMLHTTPRequest object
     */
    return function( data, textStatus, jqXHR ) {
        var jsonModel = data;
        if ( jsonModel ) {
            // save data model to the global state
            saveDataModel( type, jsonModel );
            displayResponse( response, viewModel, names, jsonModel, urls );
        }
    };
}

/**
 * Display ES search response using data model registry search model
 * @param type {string} - data type
 * @param record {BackboneCollection} - ES response converted to a Backbone.Collection object
 * @param nResults {number} - total number of search results
 * @returns {Function}
 */
function displayServerSearchModel( type, record, nResults ) {
    /**
     * Callback that is called when the call to display a search model from Data Model Registry succeeds
     * @param data {object} - data model JSON object
     * @param textStatus {string} - status of the request
     * @param jqXHR {object} - jQuery XMMLHTTPRequest object
     */
    return function ( data, textStatus, jqXHR ) {

        if ( data ) {
            // Save the model if it doesn't exist in global state yet
            saveSearchModel( type, data );
            displaySearchResponse( data, record, nResults );
        } else {
            console.log("Error: server search model for type " + type + "is empty.");
        }

    };
}

/**
 * Get the name of the data type used in the section name from the API JSON model
 * @param name {string} - name of the section of the page from the API model
 * @returns {*}
 */
function getDataModelName( name ) {

    var type,
        apiModels,
        apiPageModel,
        apiSection,
        i,
        j;
    if ( GlobalPageState ){
        apiModels = getJSONModel( "api", GlobalPageState.type, null );

        if ( apiModels ){
            for ( i = 0; i < apiModels.properties.length; i++ ) {
                apiPageModel = apiModels.properties[i];
                if ( apiPageModel && apiPageModel.page === GlobalPageState.currentPage ) {

                    for ( j = 0; j < apiPageModel.properties.length; j++ ) {
                        apiSection = apiPageModel.properties[j];
                        if ( apiSection.name === name ) {
                            type = apiSection.model;
                        }
                    }
                }
            }
        }
    }
    return type;
}

/**
 * Save DMR data model
 *  @param type {string} - data type
 * @returns {Function}
 */
function saveServerJSONModel( type ) {
    /**
     * Callback that is called when the call to fetch the model from Data Model Registry succeeds
     * @param data {object} - data model JSON object
     * @param textStatus {string} - status of the request
     * @param jqXHR {object} - jQuery XMMLHTTPRequest object
     */
    return function( data, textStatus, jqXHR ) {
        var jsonModel = data;
        if ( jsonModel ) {
            // save data model to the global state
            saveDataModel( type, jsonModel );
        }
    };
}

/**
 * Save a JSON data model to the global page state
 * @param name {string} - data type
 * @param model {object} - JSON data model
 */
function saveDataModel( name, model ) {

    if ( ! GlobalPageState ) {
        GlobalPageState = {};
    }
    if ( ! GlobalPageState.models ) {
        GlobalPageState.models = {};
    }

    if ( GlobalPageState.models && ! GlobalPageState.models[ name ] && model !== undefined ) {
        GlobalPageState.models[ name ] = model;
    }
}

/**
 * Save a JSON search model to the global page state
 * @param name {string} - data type
 * @param model {object} - JSON search model
 */
function saveSearchModel( name, model ) {

    if ( ! GlobalPageState ) {
        GlobalPageState = {};
    }
    if ( ! GlobalPageState.search ) {
        GlobalPageState.search = {};
    }

    if ( GlobalPageState.search && ! GlobalPageState.search[ name ] ) {
        GlobalPageState.search[ name ] = model;
    }
}

/**
 * Retrieve a data model from the global page state if it exists
 * @param name {string} - data type name
 * @param doRetrieve {boolean} - if true, fetch the model and save it to the global store
 * @returns {object}
 */
function getDataModel( name, doRetrieve ) {
    var model;
    if ( name ) {

        if ( ! GlobalPageState ) {
            GlobalPageState = {};
        }
        if ( ! GlobalPageState.models ) {
            GlobalPageState.models = {};
        }

        if ( GlobalPageState && GlobalPageState.models ) {
            model = GlobalPageState.models[ name ];

            // if model has not been fetched yet, retrieve it from DMR and store
            // if call to DMR fails, display an error response in the console
            if ( ! model && doRetrieve ) {
                getJSONUIModel( name, saveServerJSONModel( name ), dataNotFound( "Get JSON UI model for type " + name ) );
            }
        }
    }
    return model;
}

/**
 * Retrieve a search model from the global page state if it exists
 * @param name {string} - data type name
 * @returns {object}
 */
function getSearchModel( name ) {
    var model;
    if ( name && GlobalPageState && GlobalPageState.search ) {
        model = GlobalPageState.search[ name ];
    }
    return model;
}

/**
 * Convert a JavaScript object into a Backbone model with keys as attribute names
 * @param object {object} - JavaScript object
 * @returns {BackboneModel}
 */
function getBackboneModel( object ) {
    var bbModel = new BackboneModel,
        key;

    if ( hasKeys( object ) ) {
        for ( key in object ) {
            if ( object.hasOwnProperty( key ) ) {
                bbModel.set( key, object[ key ] );
            }
        }
    }
    return bbModel;
}

/**
 * Convert a JavaScript object into an array of Backbone models for records with attributes "name" and "value"
 * @param object {object} - JavaScript object
 * @returns {Array}
 */
function getBackboneRecordModels( object ) {
    var result = [],
        bbModel,
        key;

    if ( hasKeys( object ) ) {
        for ( key in object ) {
            if ( object.hasOwnProperty( key ) ) {
                bbModel = new BackboneModel;
                bbModel.set( "name", key );
                bbModel.set( "value", object[ key ] );
                result.push( bbModel );
            }
        }
    }
    return result;
}

/**
 * Get data type label from the global model
 * @param name
 * @returns {*}
 */
function getDataTypeLabel( name ) {

    var result,
        model,
        properties,
        data;

    model = getDataModel( name, false );

    if ( model ) {
        properties = model.properties;
        if ( properties && properties.length === 1 ) {
            data = properties[ 0 ];
            if ( data ) {
                result = data.label;
            }
        }
    }
    return result;
}

/**
 * Parse the JSON model object and return one of its top-level properties object
 * @param model {object} - JSON data model
 * @param name {string} - key name
 * @returns {*}
 */
function getModelProperty( model, name ) {

    var result,
        modelProperties,
        properties;

    if ( model ) {
        modelProperties = model.properties;
        if ( modelProperties && modelProperties.length === 1 ) {
            properties = modelProperties[ 0 ];
            if ( properties ) {
                result = properties[ name ];
            }
        }
    }
    return result;
}

/**
 * Return model properties array
 * @param model {object} - JSON data model
 * @returns {*}
 */
function getModelProperties( model ) {
    var properties,
        topProperties,
        fieldProperties;

    if ( model ) {
        properties = model.properties;
        if ( properties && properties.length === 1 ) {
            topProperties = properties[ 0 ];
            if ( topProperties ) {
                fieldProperties = topProperties.properties;
            }
        }
    }
    return fieldProperties;
}

/**
 * Return field JavaScript object from a JSON model
 * @param model {object} - JSON data model
 * @param name {string} - field name
 * @returns {*}
 */
function getFieldObjectByName( model, name ) {
    var field,
        properties,
        compoundNameArray,
        compoundName,
        compoundField;
    if ( model && name ) {

        properties = getModelProperties( model );

        if ( name.indexOf(".") > -1 ) {
            compoundNameArray = name.split(".");
            if ( compoundNameArray && compoundNameArray.length === 2 ) {
                compoundName = compoundNameArray[ 0 ];
                compoundField = getFieldProperty( properties, compoundName );
                if ( compoundField && compoundField.properties ) {
                    // Get the nested field object from properties of the compound field
                    field = getFieldProperty( compoundField.properties, compoundNameArray[ 1 ] );
                }
            }
        } else {
            field = getFieldProperty( properties, name );
        }


    }
    return field;
}

/**
 * Return one property object
 * @param properties {Array} - array of model properties
 * @param name {string} - property name
 * @returns {*}
 */
function getFieldProperty( properties, name ) {

    var field = null;

    if ( properties && name ) {
        _.each( properties, function( property ) {
            if ( property.name === name ) {
                field = property;
            }
        });
    }

    return field;
}

/**
 * Get a Backbone model from a UI model and field name
 * @param model {object} - JSON UI model
 * @param name {string} - field name (can be nested once with a dot notation)
 * @returns {*}
 */
function getFieldModelByName( model, name ) {

    var fieldModel,
        recordCollection,
        compoundNameArray,
        parentName,
        fieldName,
        parentModel;

    if ( model && name ) {

        // convert record model into a Backbone collection of field models
        recordCollection = getBackboneCollectionFromUIModel( model );

        if ( recordCollection ) {
            if ( name.indexOf(".") > -1 ) {
                compoundNameArray = name.split(".");
                if ( compoundNameArray && compoundNameArray.length === 2 ) {
                    parentName = compoundNameArray[ 0 ];
                    fieldName = compoundNameArray[ 1 ];

                    parentModel = recordCollection.findWhere( { name: parentName } );

                    if ( parentModel && parentModel.get( "type" ) === "object" && parentModel.get( "name" ) === parentName ) {
                        recordCollection = getBackboneCollection( parentModel.get("properties") );

                        fieldModel = recordCollection.findWhere( { name: fieldName } );
                    }
                }
            } else {
                fieldModel = recordCollection.findWhere( { name: name } );
            }

        }
    }

    return fieldModel;
}

/**
 * Compare categories object with stored data models
 * @param categories {object} - categories object
 * @param nModels {number} - number of data models
 */
function haveAllModels( categories, nModels ) {
    var result = false,
        modelsFound,
        localModel,
        localModels,
        category,
        types,
        type;

    // Initialize the models stored so far to zero
    modelsFound = 0;

    // Check that the stored models match models in the categories object
    if ( GlobalPageState && GlobalPageState ) {
        localModels = GlobalPageState.models;
        for ( localModel in localModels ) {

            if ( localModels.hasOwnProperty( localModel )) {
                for ( category in categories ) {
                    if ( categories.hasOwnProperty( category ) ) {
                        types = categories[ category ].types;
                        for ( type in types ) {
                            if ( types.hasOwnProperty( type ) ) {
                                if ( type === localModel ) {
                                    // model with the same name is found the global space
                                    modelsFound++;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    if ( nModels === modelsFound ) {
        result = true;
    }

    return result;
}